import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { BotPreferences } from '@brandschutz/shared';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { modalContent, modalTransition } from '@/lib/motion';
import { useAssistant } from './useAssistant';

/**
 * Chat-Container des Companion-Bots.
 *
 * - Position relativ zur Bubble (über `position` Prop).
 * - Header mit Titel + Projekt-Kontext-Toggle + Close.
 * - Body: Nachrichtenliste, partialReply als typing-Indikator.
 * - Footer: Textarea (Strg+Enter sendet).
 * - Projekt-ID: wird aus `/projects/:id` via useParams gezogen.
 */

interface Props {
  position: BotPreferences['botPosition'];
  onClose: () => void;
}

const PANEL_POSITION: Record<BotPreferences['botPosition'], string> = {
  'bottom-right': 'bottom-20 right-5',
  'bottom-left': 'bottom-20 left-5',
  'top-right': 'top-32 right-5',
  'top-left': 'top-32 left-5',
};

export function ChatPanel({ position, onClose }: Props) {
  const params = useParams<{ id?: string }>();
  const detectedProjectId = useMemo(() => {
    const id = params.id ? Number(params.id) : NaN;
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }, [params.id]);

  const [useContext, setUseContext] = useState<boolean>(true);
  const [draft, setDraft] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { messages, partialReply, isStreaming, error, send, clear } = useAssistant();

  // Auto-scroll, sobald sich Inhalt ändert.
  useEffect(() => {
    bodyRef.current?.scrollTo({
      top: bodyRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, partialReply]);

  // Focus aufs Textarea, wenn Panel sich öffnet.
  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const handleSend = (): void => {
    if (draft.trim().length === 0 || isStreaming) return;
    const pid = useContext && detectedProjectId ? detectedProjectId : undefined;
    send(draft, pid);
    setDraft('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <motion.div
      role="dialog"
      aria-label="Ignis-AI-Assistant"
      variants={modalContent}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={modalTransition}
      className={cn(
        'fixed z-50 flex w-[min(28rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-lg border border-ink-200 bg-paper shadow-elev-4 print:hidden',
        PANEL_POSITION[position],
      )}
      style={{ maxHeight: '70vh' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-ink-200 bg-ink-50 px-3 py-2">
        <div className="flex items-center gap-2">
          <Icon name="Sparkles" size={16} className="text-accent-500" />
          <span className="text-sm font-semibold text-ink-900">Ignis-AI-Assistant</span>
        </div>
        <div className="flex items-center gap-2">
          {detectedProjectId !== undefined ? (
            <button
              type="button"
              onClick={() => setUseContext((v) => !v)}
              aria-pressed={useContext}
              title={
                useContext
                  ? 'Projekt-Kontext ist aktiv (deaktivieren)'
                  : 'Projekt-Kontext aktivieren'
              }
              className={cn(
                'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px]',
                useContext
                  ? 'border-accent-500 bg-accent-50 text-accent-700'
                  : 'border-ink-300 bg-paper text-ink-600 hover:border-ink-400',
              )}
            >
              <Icon name="Link2" size={12} />
              Projekt-Kontext: {useContext ? 'an' : 'aus'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={clear}
            aria-label="Verlauf leeren"
            title="Verlauf leeren"
            className="rounded p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-800"
          >
            <Icon name="RotateCcw" size={14} />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-800"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div
        ref={bodyRef}
        className="flex-1 space-y-3 overflow-y-auto px-3 py-3"
        style={{ maxHeight: 'calc(70vh - 9rem)' }}
      >
        {messages.length === 0 && !partialReply && !error ? (
          <EmptyHint hasProject={detectedProjectId !== undefined} />
        ) : null}

        {messages.map((m, idx) => (
          <MessageBubble key={idx} role={m.role} content={m.content} />
        ))}

        {partialReply ? <MessageBubble role="assistant" content={partialReply} typing /> : null}

        {isStreaming && !partialReply ? (
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <Icon name="Loader2" size={14} className="animate-spin" />
            Antwort wird generiert…
          </div>
        ) : null}

        {error ? (
          <div className="rounded border border-danger/30 bg-red-50 px-3 py-2 text-xs text-danger">
            <div className="font-medium">Fehler</div>
            <div>{error}</div>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="border-t border-ink-200 bg-ink-50 px-3 py-2">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Frage stellen — Strg+Enter sendet"
            rows={2}
            disabled={isStreaming}
            className={cn(
              'flex-1 resize-none rounded border border-ink-300 bg-paper px-2 py-1.5 text-sm text-ink-900',
              'placeholder:text-ink-400',
              'focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500',
              'disabled:bg-ink-100 disabled:text-ink-500',
            )}
            maxLength={4000}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={isStreaming || draft.trim().length === 0}
            aria-label="Senden"
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded',
              'bg-ink-900 text-paper transition-colors hover:bg-ink-800',
              'disabled:bg-ink-400',
            )}
          >
            <Icon
              name={isStreaming ? 'Loader2' : 'Send'}
              size={16}
              className={isStreaming ? 'animate-spin' : undefined}
            />
          </button>
        </div>
        <p className="mt-1 text-[10px] text-ink-500">
          Antworten sind KI-generiert — fachliche Verantwortung liegt beim Planer.
        </p>
      </div>
    </motion.div>
  );
}

function MessageBubble({
  role,
  content,
  typing,
}: {
  role: 'user' | 'assistant';
  content: string;
  typing?: boolean;
}) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm',
          isUser ? 'bg-ink-900 text-paper' : 'border border-ink-200 bg-ink-50 text-ink-900',
        )}
      >
        {content}
        {typing ? (
          <span
            aria-hidden="true"
            className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-ink-500"
          />
        ) : null}
      </div>
    </div>
  );
}

function EmptyHint({ hasProject }: { hasProject: boolean }) {
  return (
    <div className="space-y-2 rounded border border-ink-200 bg-ink-50 px-3 py-2 text-xs text-ink-600">
      <p className="font-medium text-ink-800">Wie kann ich helfen?</p>
      <ul className="list-disc pl-4 text-ink-600">
        {hasProject ? (
          <>
            <li>„Was fehlt noch in diesem Projekt?"</li>
            <li>„Welche Pflichtfelder sind offen?"</li>
            <li>„Welche Rettungswege gelten für GK 3?"</li>
          </>
        ) : (
          <>
            <li>„Was bedeutet Gebäudeklasse GK 3 nach BayBO?"</li>
            <li>„Welche F-Klasse braucht eine tragende Wand bei GK 4?"</li>
          </>
        )}
      </ul>
    </div>
  );
}
